import { useCallback, useEffect, useState } from "react";
import { ClipboardCopy, KeyRound, Loader2, RefreshCw, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

const Row = ({ label, value, onCopy, testId }) => (
  <div className="flex items-center justify-between gap-3 rounded-lg border border-ink-600 bg-ink-900/70 p-3">
    <div className="min-w-0">
      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
      <p data-testid={testId} className="mt-1 break-all font-mono text-[12px] text-slate-200">{value}</p>
    </div>
    <Button data-testid={`${testId}-copy`} variant="ghost" size="sm" onClick={() => onCopy(value, label)}
      className="shrink-0 text-slate-400 hover:text-gold">
      <ClipboardCopy className="h-3.5 w-3.5" />
    </Button>
  </div>
);

/** A throwaway login in the demo company, so a store reviewer never sees real operator data. */
export const ReviewerAccess = ({ app, onCopy }) => {
  const [reviewer, setReviewer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/mobile-apps/${app.id}/reviewer`);
      setReviewer(data.reviewer || {});
    } catch (e) {
      setError(errMsg(e));
    }
  }, [app.id]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn) => {
    setBusy(true); setError("");
    try {
      setReviewer((await fn()).data.reviewer || {});
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const create = (rotate) => act(() =>
    api.post(`/admin/mobile-apps/${app.id}/reviewer${rotate ? "?rotate=true" : ""}`));
  const drop = () => act(() => api.delete(`/admin/mobile-apps/${app.id}/reviewer`));

  const has = Boolean(reviewer && (reviewer.email || reviewer.mode === "no_login"));

  return (
    <div data-testid="reviewer-access" className="rounded-xl border border-ink-600 bg-ink-800 p-5">
      <p className="flex items-center gap-2 font-display text-lg text-slate-100">
        <KeyRound className="h-4 w-4 text-gold" /> Reviewer access
      </p>
      <p className="mt-1 text-sm text-slate-400">
        {app.kind === "rider"
          ? "A white-label passenger app needs no account — the reviewer opens it and books. This is the line to paste into App access."
          : "A throwaway login inside the LimoLinkX Demo company: its own chauffeur, passenger and trips, so Google and Apple never see a real operator's data."}
      </p>
      {error && <p data-testid="reviewer-error" className="mt-3 text-sm text-red-400">{error}</p>}

      {reviewer === null ? (
        <Loader2 className="mt-4 h-4 w-4 animate-spin text-gold" />
      ) : has ? (
        <div className="mt-4 space-y-2">
          {reviewer.mode === "no_login" ? (
            <>
              <Row testId="reviewer-url" label="App URL" value={reviewer.url} onCopy={onCopy} />
              <p className="text-xs text-slate-500">{reviewer.note}</p>
            </>
          ) : (
            <>
              <Row testId="reviewer-email" label="Demo email" value={reviewer.email} onCopy={onCopy} />
              <Row testId="reviewer-password" label="Demo password" value={reviewer.password} onCopy={onCopy} />
              <p className="text-xs text-slate-500">
                Signs in as {reviewer.role} of {reviewer.company}. It is already in the review notes
                on the checklist below.
              </p>
            </>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            {reviewer.mode !== "no_login" && (
              <Button data-testid="reviewer-rotate" variant="outline" size="sm" disabled={busy}
                onClick={() => create(true)}>
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      : <RefreshCw className="mr-2 h-4 w-4" />} New password
              </Button>
            )}
            <Button data-testid="reviewer-remove" variant="ghost" size="sm" disabled={busy}
              onClick={drop} className="text-slate-400 hover:text-red-400">
              <Trash2 className="mr-2 h-4 w-4" /> Remove
            </Button>
          </div>
        </div>
      ) : (
        <Button data-testid="reviewer-create" disabled={busy} onClick={() => create(false)}
          className="mt-4 bg-gold text-ink-900 hover:bg-gold-light">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
          {app.kind === "rider" ? "Prepare reviewer note" : "Create demo login"}
        </Button>
      )}
    </div>
  );
};
